import {
    Typography,
    FormControlLabel,
    Switch,
    Grid,
    TextField,
    IconButton,
    Button,
    Box,
    Stack,
    FormHelperText,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import { FormikProps } from 'formik';
import { CustomerTypes } from '../../../../../types';
import { formatUsPhone, isValidUsPhone } from '../../../../../helpers/phone';
import { EmailChipsInput } from '../../../../../components/email-chips-input';
import { SettingsFieldCard } from './settings-field-card';

interface Props {
    formik: FormikProps<CustomerTypes.OnboardingForm>;
    /**
     * Gates only the v2 catalog settings on this tab (the Notify On Dispatch
     * flag and its phone/email recipient lists). The SOS Passcode is stored on
     * the Customer document itself, is not a catalog setting and has no v2
     * editor, so it stays editable regardless. Defaults to disabled so a
     * caller that forgets the prop cannot reopen a legacy settings write path.
     */
    settingsDisabled?: boolean;
}

export const SosSettingsTab: React.FC<Props> = ({ formik, settingsDisabled = true }) => {
    const { values, touched, errors, getFieldProps } = formik;

    const handleFieldChange = (field: string, value: any) => {
        formik.setFieldValue(field, value);
    };

    const dispatchPhones = values?.callOnDispatchPhoneNumbers ?? [];

    const updateDispatchPhone = (index: number, raw: string) => {
        const next = [...dispatchPhones];
        next[index] = formatUsPhone(raw);
        formik.setFieldValue('callOnDispatchPhoneNumbers', next);
    };

    const removeDispatchPhone = (index: number) => {
        const next = dispatchPhones.filter((_, i) => i !== index);
        formik.setFieldValue('callOnDispatchPhoneNumbers', next);
    };

    const addDispatchPhone = () => {
        formik.setFieldValue('callOnDispatchPhoneNumbers', [...dispatchPhones, '+1']);
    };

    return (
        <>
            <Typography fontWeight={500} fontSize={18} mt={2}>
                SOS Alerts
            </Typography>
            <Typography fontSize={14} mb={1} color="text.secondary">
                Configure how SOS alerts behave for this organization.
            </Typography>

            {/* SOS Passcode */}
            <SettingsFieldCard>
                <Grid container direction="column" sx={{ width: 1 }}>
                    <Grid maxWidth="sm" sx={{ width: 1 }}>
                        <Typography fontWeight="600" fontSize={16}>SOS Passcode</Typography>
                        <Typography color="textSecondary" variant="body2">
                            A shared passcode that team members provide to POM Safe Dispatch to confirm their safety during an SOS Alert or Check-on-Me escalation.
                        </Typography>
                    </Grid>
                    <TextField
                        size="small"
                        placeholder="Enter passcode"
                        sx={{ mt: 2, maxWidth: 300 }}
                        {...getFieldProps('passCode')}
                        error={Boolean(errors.passCode)}
                        helperText={touched.passCode && errors.passCode}
                    />
                </Grid>
            </SettingsFieldCard>

            {/* Notify On Dispatch (settings field: callOnDispatch) */}
            <SettingsFieldCard>
                <Grid container direction="column" sx={{ width: 1 }}>
                    <Grid container direction="row">
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={values?.callOnDispatch ?? false}
                                    onChange={(e) => handleFieldChange('callOnDispatch', e.target.checked)}
                                    disabled={settingsDisabled}
                                />
                            }
                            label=""
                            sx={{ my: 'auto' }}
                        />
                        <Grid maxWidth="sm" sx={{ width: 1 }}>
                            <Typography fontWeight="600" fontSize={16}>Notify On Dispatch</Typography>
                            <Typography color="textSecondary" variant="body2">
                                When enabled, an automated phone call will be placed to each phone number below, and a dispatch summary email will be sent to each email address below, whenever an SOS alert is dispatched. Add at least one phone number or email address.
                            </Typography>
                        </Grid>
                    </Grid>

                    {values?.callOnDispatch && (
                        <Box sx={{ mt: 2, ml: 6 }}>
                            <Typography fontSize={13} fontWeight={600} mb={1} color="text.secondary">
                                Phone Numbers
                            </Typography>
                            <Stack spacing={1.5} sx={{ maxWidth: 420 }}>
                                {dispatchPhones.map((phone, index) => {
                                    const showError = !isValidUsPhone(phone);
                                    return (
                                        <Stack key={index} direction="row" spacing={1} alignItems="center">
                                            <TextField
                                                size="small"
                                                value={phone || '+1'}
                                                onChange={(e) => updateDispatchPhone(index, e.target.value)}
                                                disabled={settingsDisabled}
                                                InputProps={{ readOnly: settingsDisabled }}
                                                error={showError}
                                                helperText={showError ? 'Must be +1 followed by 10 digits' : ''}
                                                fullWidth
                                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', fontSize: '14px' } }}
                                            />
                                            <IconButton
                                                size="small"
                                                onClick={() => removeDispatchPhone(index)}
                                                aria-label="remove phone number"
                                                disabled={settingsDisabled}
                                            >
                                                <ClearIcon sx={{ fontSize: 18 }} />
                                            </IconButton>
                                        </Stack>
                                    );
                                })}
                            </Stack>
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={addDispatchPhone}
                                disabled={settingsDisabled}
                                sx={{ mt: 1.5 }}
                            >
                                Add Phone Number
                            </Button>
                            {/* Hidden until the API returns the field: a settings
                                save against an older API would silently strip any
                                emails entered here. */}
                            {values?.callOnDispatchEmails !== undefined && (
                                <>
                                    <Typography fontSize={13} fontWeight={600} mt={2.5} mb={1} color="text.secondary">
                                        Email Addresses
                                    </Typography>
                                    <Box sx={{ maxWidth: 420 }}>
                                        <EmailChipsInput
                                            value={values?.callOnDispatchEmails ?? []}
                                            onChange={(emails) => handleFieldChange('callOnDispatchEmails', emails)}
                                            disabled={settingsDisabled}
                                            label=""
                                            placeholder="Type an address; separate with a comma or semicolon"
                                            helperText="Each address receives a dispatch summary email."
                                        />
                                    </Box>
                                </>
                            )}
                            {typeof errors.callOnDispatchPhoneNumbers === 'string' && (
                                <FormHelperText error sx={{ mt: 1.5 }}>
                                    {errors.callOnDispatchPhoneNumbers}
                                </FormHelperText>
                            )}
                        </Box>
                    )}
                </Grid>
            </SettingsFieldCard>
        </>
    );
};
